import React, { useEffect, useRef } from 'react';
import { listRubricQuestions, normalizeQuestionId } from './tableToOneLine';

function escapeFocusRe(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const ANY_DIGIT = '0-9۰-۹٠-٩';

function latinNumber(raw) {
  if (raw === 'ا' || raw === 'أ' || raw === 'آ') return 1;
  const map = {
    '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4', '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9',
    '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4', '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9'
  };
  const n = parseInt(String(raw).replace(/[۰-۹٠-٩]/g, (ch) => map[ch] || ch), 10);
  return Number.isFinite(n) ? n : null;
}

// Urdu transcripts write "سوال نمبر 10", "سوال نمبر :1", "سوال ۱۰" or
// "سوال نمبر ۱۱", not "Q10". Digits may be Latin, Urdu, or Arabic-Indic.
function urduQuestionHits(lines) {
  const re = new RegExp(
    `سوال\\s*(?:نمبر|نمره)?\\s*[:：\\-۔.\\s]*([${ANY_DIGIT}]+|ا|أ|آ)(?![${ANY_DIGIT}])`,
    'g'
  );
  const hits = [];
  lines.forEach((line, lineIndex) => {
    re.lastIndex = 0;
    let found;
    while ((found = re.exec(line))) {
      const token = found[1];
      if (/^[اأآ]$/.test(token) && /[\u0621-\u064A\u0670-\u06D3\u06D5]/.test(line.charAt(found.index + found[0].length))) {
        continue;
      }
      const num = latinNumber(token);
      if (!num) continue;
      hits.push({ line: lineIndex, index: found.index, num });
    }
  });
  return hits;
}

function offsetAt(lines, line, col) {
  let offset = 0;
  for (let i = 0; i < line; i++) offset += lines[i].length + 1;
  return offset + col;
}

function questionMatchers(questionId) {
  const id = normalizeQuestionId(questionId);
  const match = String(id || '').match(/^q(\d+)(?:\.(.+))?$/i);
  if (!match) return null;
  const num = match[1];
  const sub = match[2] || '';
  const part = escapeFocusRe(sub);
  // "Q5.b", "Q.5.b", "Q 5 (b)", "Q: 5 (b)", and "Q: 5 a)" are the same part.
  // A bare "Q5" does not open "(b)" or "a)".
  const full = sub
    ? new RegExp(`^\\s*Q[.:]?\\s*${num}\\s*(?:[.)]\\s*|\\(\\s*)?${part}\\b\\)?`, 'i')
    : new RegExp(`^\\s*Q[.:]?\\s*${num}\\b(?!\\s*(?:(?:[.)]\\s*|\\(\\s*)[A-Za-z0-9]|[A-Za-z]\\s*\\)))`, 'i');
  const partOnly = sub
    ? new RegExp(`^\\s*${part}\\s*[.)]\\s+`, 'i')
    : null;
  return { num, sub, full, partOnly };
}

const QUESTION_LINE = /^\s*Q[.:]?\s*\d+/i;

function isNextQuestion(line, full, partOnly) {
  if (full.test(line) || (partOnly && partOnly.test(line))) return false;
  if (QUESTION_LINE.test(line)) return true;
  return /^\s*(?:[ivx]+|[a-z]|\d+)\s*[.)]\s+\S/i.test(line);
}

function spanFromLine(lines, startLine, full, partOnly) {
  const openedOnFull = full.test(lines[startLine]);
  let endLine = lines.length;
  for (let i = startLine + 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    if (openedOnFull) {
      if (QUESTION_LINE.test(lines[i])) { endLine = i; break; }
      continue;
    }
    if (isNextQuestion(lines[i], full, partOnly)) { endLine = i; break; }
  }
  let start = 0;
  for (let i = 0; i < startLine; i++) start += lines[i].length + 1;
  let end = start;
  const last = Math.min(endLine, lines.length) - 1;
  for (let i = startLine; i <= last; i++) end += lines[i].length + (i < last ? 1 : 0);
  return { start, end: Math.max(start, end) };
}

// Where a question's block starts and ends in a transcript or rubric.
// Prefers "Q2.v" / "Q.2.v". Falls back to a line that is only the part,
// such as "v. Solve …", which is how the OCR often writes a sub-question.
export function findQuestionSpan(text, questionId) {
  const parts = questionMatchers(questionId);
  const source = String(text || '');
  if (!parts || !source) return null;
  const { num, sub, full, partOnly } = parts;
  const lines = source.split('\n');
  let startLine = lines.findIndex((line) => full.test(line));
  if (startLine < 0 && partOnly) {
    const parent = new RegExp(`^\\s*Q[.:]?\\s*${num}\\b`, 'i');
    const otherQuestion = new RegExp(`^\\s*Q[.:]?\\s*(?!${num}\\b)\\d+`, 'i');
    const parentAt = lines.findIndex((line) => parent.test(line));
    if (parentAt >= 0) {
      let regionEnd = lines.length;
      for (let i = parentAt + 1; i < lines.length; i++) {
        if (otherQuestion.test(lines[i])) { regionEnd = i; break; }
      }
      for (let i = parentAt; i < regionEnd; i++) {
        if (partOnly.test(lines[i])) { startLine = i; break; }
      }
    }
  }
  if (startLine < 0 && partOnly) startLine = lines.findIndex((line) => partOnly.test(line));
  let openedOnUrdu = false;
  let urduStartCol = 0;
  let urduEnd = null;
  if (startLine < 0) {
    const hits = urduQuestionHits(lines);
    const hitAt = hits.findIndex((hit) => hit.num === Number(num));
    if (hitAt >= 0) {
      const hit = hits[hitAt];
      openedOnUrdu = true;
      startLine = hit.line;
      urduStartCol = hit.index;
      urduEnd = hits[hitAt + 1] || null;
      if (sub && partOnly) {
        const limit = urduEnd ? urduEnd.line : lines.length;
        for (let i = hit.line; i < limit; i++) {
          if (partOnly.test(lines[i])) {
            startLine = i;
            urduStartCol = 0;
            break;
          }
        }
      }
    }
  }
  if (startLine < 0) return null;
  if (openedOnUrdu) {
    let endLine = urduEnd ? urduEnd.line : lines.length;
    let endCol = urduEnd ? urduEnd.index : 0;
    const stop = urduEnd ? urduEnd.line : lines.length;
    for (let i = startLine + 1; i < stop; i++) {
      if (QUESTION_LINE.test(lines[i])) {
        endLine = i;
        endCol = 0;
        break;
      }
    }
    const start = offsetAt(lines, startLine, urduStartCol);
    const end = endLine >= lines.length ? source.length : offsetAt(lines, endLine, endCol);
    return { start, end: Math.max(start, end) };
  }
  return spanFromLine(lines, startLine, full, partOnly);
}

// Every full heading for this id. A repeated "Q.2.ii" is two blocks.
// A part-only line ("ii. Solve") stays a single fallback, same as findQuestionSpan.
export function findQuestionSpans(text, questionId) {
  const parts = questionMatchers(questionId);
  const source = String(text || '');
  if (!parts || !source) return [];
  const lines = source.split('\n');
  const starts = [];
  lines.forEach((line, index) => {
    if (parts.full.test(line)) starts.push(index);
  });
  if (!starts.length) {
    const single = findQuestionSpan(text, questionId);
    return single ? [single] : [];
  }
  return starts.map((startLine) => spanFromLine(lines, startLine, parts.full, parts.partOnly));
}

// "QN.INC (labeled as Q.2.ii)" — the number the student wrote, when it is
// not the rubric id this row is scored on.
const LABELED_AS_RE = /labeled\s+as\s+(Q\.?\s*\d+(?:\s*[.)]\s*[A-Za-z0-9\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]+)?)/i;

export function mislabeledQuestionId(deduction) {
  const labeled = String(deduction || '').match(LABELED_AS_RE);
  if (!labeled) return null;
  return normalizeQuestionId(labeled[1]);
}

export function rowDeduction(header, cells) {
  const idx = (header || []).findIndex((h) => {
    const key = String(h || '').replace(/\*\*/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
    return key === 'deduction' || key === 'mistakes';
  });
  const cell = idx >= 0 ? (cells || [])[idx] : (cells || [])[2];
  return String(cell || '').replace(/\*\*/g, '');
}

const FOCUS_STOP = new Set([
  'that', 'this', 'with', 'from', 'they', 'them', 'their', 'have', 'been', 'into', 'your',
  'what', 'when', 'where', 'which', 'does', 'write', 'name', 'define', 'about', 'answer',
  'marks', 'model', 'rubric', 'called', 'through', 'because', 'there', 'these', 'those',
  'such', 'also', 'only', 'each', 'than', 'then', 'some', 'more', 'most', 'very', 'will',
  'would', 'could', 'should', 'other', 'after', 'before', 'between', 'under', 'over',
  'following', 'question', 'correct', 'example', 'given', 'state', 'explain', 'describe'
]);

function focusTokens(text) {
  const raw = String(text || '').toLowerCase().match(/[a-z][a-z0-9]{3,}|[\u0600-\u06FF]{3,}/g) || [];
  const out = [];
  raw.forEach((word) => {
    if (FOCUS_STOP.has(word)) return;
    let token = word;
    if (/^[a-z]/.test(token) && token.length > 4 && token.endsWith('s') && !token.endsWith('ss')) {
      token = token.slice(0, -1);
    }
    if (/^[a-z]/.test(token) && token.length < 4) return;
    out.push(token);
  });
  return out;
}

function overlapScore(block, reference) {
  const have = new Set(focusTokens(block));
  const seen = new Set();
  let score = 0;
  focusTokens(reference).forEach((token) => {
    if (seen.has(token) || !have.has(token)) return;
    seen.add(token);
    score += token.length;
  });
  return score;
}

// Question line plus Model Answer. The breakdown table is generic and is left out.
function rubricReference(rubric, questionId) {
  const span = findQuestionSpan(rubric, questionId);
  if (!span) return '';
  const lines = String(rubric || '').slice(span.start, span.end).split('\n');
  const header = lines[0] || '';
  const modelAt = lines.findIndex((line) => /^\s*model\s*answer\s*:/i.test(line));
  if (modelAt < 0) return header;
  const body = [];
  for (let i = modelAt; i < lines.length; i++) {
    if (i > modelAt && /^\s*rubrics?\s*breakdown\b/i.test(lines[i])) break;
    body.push(lines[i]);
  }
  return `${header}\n${body.join('\n')}`;
}

// Each repeated heading is kept by the rubric question whose wording it matches.
function chooseDuplicateSpan(spans, source, rubric, clickedId) {
  const questions = listRubricQuestions(rubric).map((item) => item.id);
  if (questions.indexOf(clickedId) < 0) questions.push(clickedId);
  const refs = new Map();
  questions.forEach((qid) => refs.set(qid, rubricReference(rubric, qid)));
  const clickedRef = refs.get(clickedId) || '';
  if (!clickedRef.trim()) return spans[0];

  let claimed = null;
  let claimedScore = 0;
  spans.forEach((span) => {
    const body = source.slice(span.start, span.end);
    let owner = null;
    let ownerScore = 0;
    refs.forEach((ref, qid) => {
      if (!ref) return;
      const score = overlapScore(body, ref);
      if (score > ownerScore) {
        ownerScore = score;
        owner = qid;
      }
    });
    if (owner !== clickedId) return;
    const clickedScore = overlapScore(body, clickedRef);
    if (clickedScore > claimedScore) {
      claimedScore = clickedScore;
      claimed = span;
    }
  });
  if (claimed) return claimed;

  let best = null;
  let bestScore = 0;
  spans.forEach((span) => {
    const score = overlapScore(source.slice(span.start, span.end), clickedRef);
    if (score > bestScore) {
      bestScore = score;
      best = span;
    }
  });
  return best || spans[0];
}

// Rubric id first. The written label is used only when that id is absent
// from the transcript. When that heading is written twice, the block that
// matches this row's question and model answer is the one that opens.
export function findTranscriptSpan(text, questionId, deduction, rubricText) {
  const id = normalizeQuestionId(questionId);
  if (!id) return null;
  let spans = findQuestionSpans(text, id);
  if (!spans.length) {
    const written = mislabeledQuestionId(deduction);
    if (!written || written === id) return null;
    spans = findQuestionSpans(text, written);
  }
  if (spans.length <= 1) return spans[0] || null;
  if (!String(rubricText || '').trim()) return spans[0];
  return chooseDuplicateSpan(spans, String(text || ''), rubricText, id);
}

// Which photo in the open gallery holds this transcript span.
// A "=== Page N ===" or "--- Image_1.jpg ---" marker wins. Transcripts
// that were saved without those markers are split evenly across the
// gallery. The photo is not zoomed.
export function transcriptPageIndex(text, span, images) {
  const list = Array.isArray(images) ? images : [];
  const count = list.length;
  const source = String(text || '');
  if (!span || !source || count === 0) return -1;
  const markers = [];
  const pageRe = /^[ \t]*===\s*Page\s+(\d+)(?:\s*===)?[ \t]*$/gim;
  const fileRe = /^[ \t]*---\s*([^\n]*?\.(?:jpe?g|png|webp|gif|bmp|tiff?))\s*---[ \t]*$/gim;
  let found;
  while ((found = pageRe.exec(source))) {
    markers.push({ kind: 'page', page: Number(found[1]), start: found.index });
  }
  while ((found = fileRe.exec(source))) {
    markers.push({ kind: 'file', name: found[1].trim(), start: found.index });
  }
  markers.sort((a, b) => a.start - b.start);
  let current = null;
  for (let i = 0; i < markers.length; i++) {
    if (markers[i].start <= span.start) current = markers[i];
    else break;
  }
  if (current && current.kind === 'file') {
    const wanted = current.name.toLowerCase();
    const byName = list.findIndex((img) => String(img.name || '').toLowerCase() === wanted);
    if (byName >= 0) return byName;
  }
  if (current && current.kind === 'page') {
    const idx = current.page - 1;
    if (idx >= 0 && idx < count) return idx;
  }
  if (count < 2) return -1;
  const total = Math.max(source.split('\n').length, 1);
  const line = source.slice(0, Math.max(0, span.start)).split('\n').length - 1;
  return Math.min(count - 1, Math.max(0, Math.floor((line / total) * count)));
}

export function revealTextareaSpan(el, start, end) {
  if (!el) return;
  const value = el.value || '';
  const from = Math.max(0, Math.min(start, value.length));
  const to = Math.max(from, Math.min(end, value.length));
  try { el.focus({ preventScroll: true }); } catch (_) { el.focus(); }
  el.setSelectionRange(from, to);
  const style = window.getComputedStyle(el);
  const lineHeight = parseFloat(style.lineHeight) || ((parseFloat(style.fontSize) || 14) * 1.5);
  const pad = parseFloat(style.paddingTop) || 0;
  const line = value.slice(0, from).split('\n').length - 1;
  el.scrollTop = Math.max(0, pad + line * lineHeight - lineHeight);
}

// A textarea paints its selection only while it is focused. The mark is drawn
// on top of the text so the transcript and the rubric can both stay highlighted.
export function MarkedTextarea({ mark, extraMarks, scrollTick, textareaRef, style, value, onScroll, markColor, ...rest }) {
  const backdropRef = useRef(null);
  const innerRef = useRef(null);
  const setTextarea = (node) => {
    innerRef.current = node;
    if (typeof textareaRef === 'function') textareaRef(node);
    else if (textareaRef) textareaRef.current = node;
  };
  const syncBackdrop = () => {
    const ta = innerRef.current;
    const root = backdropRef.current;
    if (!ta || !root) return;
    const cs = window.getComputedStyle(ta);
    root.style.width = `${ta.clientWidth}px`;
    root.style.height = `${ta.clientHeight}px`;
    root.style.paddingTop = cs.paddingTop;
    root.style.paddingRight = cs.paddingRight;
    root.style.paddingBottom = cs.paddingBottom;
    root.style.paddingLeft = cs.paddingLeft;
    root.style.font = cs.font;
    root.style.lineHeight = cs.lineHeight;
    root.style.letterSpacing = cs.letterSpacing;
    root.scrollTop = ta.scrollTop;
    root.scrollLeft = ta.scrollLeft;
  };
  const handleScroll = (e) => {
    syncBackdrop();
    if (onScroll) onScroll(e);
  };
  useEffect(() => {
    const ta = innerRef.current;
    if (!ta || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => syncBackdrop());
    observer.observe(ta);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    let frame = 0;
    const align = () => {
      const root = backdropRef.current;
      const ta = innerRef.current;
      const marked = root && root.querySelector('mark');
      if (!ta || !root || !marked) return;
      syncBackdrop();
      ta.scrollTop = Math.max(0, marked.offsetTop - 16);
      root.scrollTop = ta.scrollTop;
    };
    align();
    frame = requestAnimationFrame(align);
    return () => cancelAnimationFrame(frame);
  }, [scrollTick, mark && mark.start, mark && mark.end]);
  const text = String(value || '');
  const start = mark ? Math.max(0, Math.min(mark.start, text.length)) : 0;
  const end = mark ? Math.max(start, Math.min(mark.end, text.length)) : 0;
  const spans = (Array.isArray(extraMarks) ? extraMarks : [])
    .map((item) => ({
      start: Math.max(0, Math.min(Number(item.start) || 0, text.length)),
      end: Math.max(0, Math.min(Number(item.end) || 0, text.length))
    }))
    .filter((item) => item.end > item.start)
    .sort((a, b) => a.start - b.start);
  const painted = [];
  let cursor = 0;
  const pushPlain = (to) => {
    if (to > cursor) painted.push(text.slice(cursor, to));
    cursor = to;
  };
  for (const span of spans) {
    const from = Math.max(span.start, cursor);
    const to = span.end;
    if (to <= cursor) continue;
    pushPlain(from);
    painted.push(
      <mark key={`chatter-${from}`} style={{ background: 'rgba(245, 158, 11, 0.55)', color: 'transparent', borderRadius: 2 }}>{text.slice(from, to)}</mark>
    );
    cursor = to;
  }
  pushPlain(text.length);
  const outer = style || {};
  return (
    <div style={{ flex: outer.flex || 1, minHeight: outer.minHeight || 0, height: outer.height, position: 'relative', width: '100%', background: outer.background || 'transparent', borderRadius: outer.borderRadius, display: 'flex', flexDirection: 'column' }}>
      <div
        ref={backdropRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 2,
          overflow: 'hidden',
          pointerEvents: 'none',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          color: 'transparent',
          boxSizing: 'border-box'
        }}
      >
        {spans.length ? painted : (
          <>
            {text.slice(0, start)}
            {end > start ? (
              <mark style={{ background: markColor || 'rgba(245, 158, 11, 0.45)', color: 'transparent', borderRadius: 2 }}>{text.slice(start, end)}</mark>
            ) : null}
            {text.slice(end)}
          </>
        )}
      </div>
      <textarea
        {...rest}
        ref={setTextarea}
        value={value}
        wrap="soft"
        onScroll={handleScroll}
        style={{
          ...outer,
          flex: 1,
          minHeight: 0,
          height: 'auto',
          background: 'transparent',
          position: 'relative',
          zIndex: 1,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'break-word',
          wordBreak: 'break-word'
        }}
      />
    </div>
  );
}
